import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams, Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { CheckCircle, Loader2, Mail, Clock } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PreOrderSuccessPage = () => {
  const { preorderId } = useParams();
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);

  useEffect(() => {
    checkPaymentStatus();
  }, [preorderId]);

  const checkPaymentStatus = async () => {
    try {
      const response = await axios.get(`${API}/preorders/${preorderId}/status`);
      setStatus(response.data);
    } catch (error) {
      console.error('Failed to check status');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD' }).format(amount);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-green-50 to-white">
        <Loader2 className="h-8 w-8 animate-spin text-green-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-green-50 to-white flex items-center justify-center p-4">
      <Card className="max-w-md w-full">
        <CardContent className="p-8 text-center">
          {status?.payment_status === 'paid' ? (
            <>
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="h-8 w-8 text-green-600" />
              </div>
              <h1 className="text-2xl font-bold text-stone-800 mb-2">Pre-Order Confirmed!</h1>
              <p className="text-stone-600 mb-4">
                Thank you for your order for <strong>{status.child_name}</strong>
              </p>
              <p className="text-2xl font-bold text-green-600 mb-6">
                {formatCurrency(status.total)}
              </p>
              
              <div className="bg-amber-50 rounded-lg p-4 mb-6 text-left">
                <h3 className="font-semibold text-amber-800 mb-2 flex items-center gap-2">
                  <Clock className="h-4 w-4" />
                  What's Next?
                </h3>
                <ol className="text-sm text-amber-700 space-y-2 list-decimal list-inside">
                  <li>Your photos will be taken on the scheduled date</li>
                  <li>We'll edit and prepare your images</li>
                  <li>You'll receive an email when your photos are ready to review</li>
                  <li>Choose which photos go with your products and approve your order</li>
                </ol>
              </div>

              <div className="flex items-center justify-center gap-2 text-sm text-stone-500">
                <Mail className="h-4 w-4" />
                <span>Confirmation sent to your email</span>
              </div>

              {/* Place Another Order */}
              {status.event_slug && (
                <div className="mt-6 pt-4 border-t">
                  <Link to={`/preorder/${status.event_slug}`}>
                    <Button variant="outline" className="w-full">
                      Place Another Order
                    </Button>
                  </Link>
                </div>
              )}
            </>
          ) : (
            <>
              <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Clock className="h-8 w-8 text-amber-600" />
              </div>
              <h1 className="text-2xl font-bold text-stone-800 mb-2">Payment Processing</h1>
              <p className="text-stone-600 mb-4">
                We're confirming your payment. This may take a moment.
              </p>
              <Button onClick={checkPaymentStatus} variant="outline">
                Check Status
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PreOrderSuccessPage;
